import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { api } from '../services/api'
import SubscriptionMaintenance from '../components/SubscriptionMaintenance'
import PageHeader from '../nexus/components/PageHeader'
import StatCard from '../nexus/components/StatCard'
import { useAnalyticsPeriod, type AnalyticsPeriod as Period } from '../lib/analytics-period'

interface Stats {
  period: Period
  total_users: number
  total_sessions: number
  total_messages: number
  user_messages: number
  assistant_messages: number
  system_messages: number
  upvotes: number
  downvotes: number
  user_breakdown: {
    free_trial: number
    help_bot: number
    subscribed: number
    staff: number
  }
}

const fmt = (n?: number) => (n == null ? '--' : n.toLocaleString())

export default function Dashboard() {
  const navigate = useNavigate()
  const { period } = useAnalyticsPeriod()
  const { data: stats, isLoading, error, refetch } = useQuery<Stats>({
    queryKey: ['stats', period],
    queryFn: () => api.getStats(period),
    staleTime: 30000,
  })

  if (error) {
    return (
      <div className="p-6">
        <PageHeader title="Dashboard" />
        <div className="card bg-base-100">
          <div className="card-body items-center text-center gap-3 py-12">
            <span className="iconify lucide--triangle-alert text-error size-10" />
            <h2 className="card-title">Failed to load statistics</h2>
            <p className="text-base-content/70">{error instanceof Error ? error.message : 'Unknown error'}</p>
            <button className="btn btn-primary btn-sm mt-2" onClick={() => refetch()}>Try again</button>
          </div>
        </div>
      </div>
    )
  }

  const avgPerSession = stats && stats.total_sessions > 0 ? (stats.total_messages / stats.total_sessions).toFixed(1) : '--'

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Dashboard" subtitle="Overview of users, conversations, and feedback." />
      </div>

      {/* Stat grid */}
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <div className="dropdown w-full">
          <button type="button" tabIndex={0} className="w-full text-left" aria-label="Show total user breakdown">
            <StatCard title="Total Users" value={isLoading ? '--' : fmt(stats?.total_users)} icon="lucide--users" tone="primary" sub="click for breakdown" />
          </button>
          {!isLoading && stats && (
            <div tabIndex={0} className="dropdown-content z-20 mt-2 card card-compact w-full min-w-64 bg-base-100 shadow-xl border border-base-300">
              <div className="card-body">
                <h3 className="font-semibold">User breakdown</h3>
                {([
                  ['Free Trial', stats.user_breakdown.free_trial],
                  ['Helpbot only', stats.user_breakdown.help_bot],
                  ['Subscribed', stats.user_breakdown.subscribed],
                  ['Staff', stats.user_breakdown.staff],
                ] as const).map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-6 text-sm">
                    <span className="text-base-content/70">{label}</span>
                    <span className="font-mono font-semibold">{fmt(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <StatCard title="Total Sessions" value={isLoading ? '--' : fmt(stats?.total_sessions)} icon="lucide--messages-square" tone="info" />
        <StatCard title="User Messages" value={isLoading ? '--' : fmt(stats?.total_messages)} icon="lucide--message-square" tone="accent" />
        <StatCard title="Avg / Session" value={isLoading ? '--' : avgPerSession} icon="lucide--activity" tone="secondary" sub="user messages per session" />
      </div>

      {/* Feedback */}
      <div className="grid gap-5 sm:grid-cols-2">
        <StatCard title="Upvotes" value={fmt(stats?.upvotes ?? 0)} icon="lucide--thumbs-up" tone="success" sub="positive feedback" />
        <StatCard title="Downvotes" value={fmt(stats?.downvotes ?? 0)} icon="lucide--thumbs-down" tone="error" sub="click to view details →" onClick={() => navigate('/downvotes')} />
      </div>

      <SubscriptionMaintenance />

      {/* Usage analytics */}
      <div className="card bg-base-100">
        <div className="card-body">
          <h2 className="card-title">Usage analytics</h2>
          <p className="text-base-content/60 text-sm">
            Detailed usage metrics — per-org breakdowns, token spend, source click tracking, response quality, and daily query volume.
          </p>
          <button type="button" onClick={() => navigate('/analytics')} className="btn btn-primary btn-sm w-fit mt-2">
            <span className="iconify lucide--chart-no-axes-combined size-4" /> View analytics
          </button>
        </div>
      </div>
    </div>
  )
}
